import './SelectField.scss';
import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { ReactElement, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { GroupBase, InputActionMeta, SingleValue } from 'react-select';
import { CreatableSelectArea, CreatableSelectAreaProps } from 'src/modules/common/components/CreatableSelectArea';
import { FormError } from 'src/modules/form/types/FormError';

type Props<
  TData,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<CreatableSelectAreaProps<Option, false, Group>,
| 'id'
| 'name'
| 'isMulti'
| 'onChange'
| 'onBlur'
| 'onInputChange'
| 'inputValue'
| 'value'
| 'invalid'
> & {
  readonly field: FieldPath<TData, FormError, Option | null>;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const CreatableSelectField = <
  TData,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>>(
  {
    field,
    renderError = renderErrorDefault,
    ...props
  }: Props<TData, Option, Group>): ReactElement => {
  const { meta, value, error } = useFormField(field);

  const [search, setSearch] = useState('');

  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const handleChange = useCallback((selectedItem: SingleValue<Option>) => {
    field.form.change(pipe(
      field.form.currentState,
      field.value.set(selectedItem),
    ));
  }, [field]);

  const handleBlur = useCallback(() => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));
  }, [field]);

  const onInputChange = useCallback((newValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action !== 'input-blur' && actionMeta.action !== 'menu-close') {
      setSearch(newValue);
    }
  }, []);

  return (
    <div className="bp-select-field">
      <CreatableSelectArea
        {...props}
        id={`${field.form.name}.${field.path}`}
        name={field.path}
        isMulti={false}
        onChange={handleChange}
        onBlur={handleBlur}
        onInputChange={onInputChange}
        inputValue={search}
        value={value}
        invalid={invalid}
      />

      <div className="bp-select-field__error">
        {error && invalid ? renderError(error) : null}
      </div>
    </div>
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
);
